import React, { useState } from 'react';
import { Inbox, AlertCircle, UserCheck, RefreshCw, Clock, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';
import { useAuth } from '../../../context/AuthContext';
import { useServiceRequestInbox, useAdminDepartments } from '../../service-requests/serviceRequestHooks';
import ServiceRequestInboxDetailPanel from '../../service-requests/ServiceRequestInboxDetailPanel';

const STATUS_PILL = {
  submitted:   { icon: Clock,        text: 'text-blue-500',   bg: 'bg-blue-100 dark:bg-blue-900/30',   label: 'Submitted' },
  in_progress: { icon: ArrowRight,   text: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', label: 'In progress' },
  completed:   { icon: CheckCircle2, text: 'text-green-500',  bg: 'bg-green-100 dark:bg-green-900/30', label: 'Completed' },
  cancelled:   { icon: XCircle,      text: 'text-red-500',    bg: 'bg-red-100 dark:bg-red-900/30',     label: 'Cancelled' },
};

export default function ServiceRequestInboxPage() {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = theme === 'dark';
  const [filters, setFilters] = useState({});
  const [selected, setSelected] = useState(null);
  const { data, isLoading, refetch, isFetching } = useServiceRequestInbox(filters);
  const { data: deptData } = useAdminDepartments();

  const rows = data?.service_requests || [];
  const showAdminFilters = currentUser?.admin || ['director', 'manager'].includes(currentUser?.role);

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-medium font-rasa flex items-center gap-2">
            <Inbox size={22} /> Service request inbox
          </h1>
          <p className={`text-sm font-rasa mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Requests routed to your department or assigned to you.
          </p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          className={`inline-flex items-center gap-1 text-xs font-rasa px-3 py-1.5 rounded-md ${
            isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <RefreshCw size={12} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <FilterChip
          active={!filters.status && !filters.needs_triage && !filters.assigned_to_me}
          onClick={() => setFilters({})}
          label="All"
          isDark={isDark}
        />
        <FilterChip
          active={!!filters.assigned_to_me}
          onClick={() => setFilters({ assigned_to_me: true })}
          label="My assignments"
          icon={UserCheck}
          isDark={isDark}
        />
        <FilterChip
          active={!!filters.needs_triage}
          onClick={() => setFilters({ needs_triage: true })}
          label="Needs triage"
          icon={AlertCircle}
          isDark={isDark}
        />
        {Object.keys(STATUS_PILL).map((s) => (
          <FilterChip
            key={s}
            active={filters.status === s}
            onClick={() => setFilters({ status: s })}
            label={STATUS_PILL[s].label}
            isDark={isDark}
          />
        ))}

        {showAdminFilters && (
          <select
            value={filters.department_slug || ''}
            onChange={(e) => setFilters({ ...filters, department_slug: e.target.value || undefined })}
            className={`text-xs font-rasa rounded-full border px-3 py-1 ${
              isDark ? 'bg-gray-900 border-gray-700 text-gray-200' : 'bg-white border-gray-300'
            }`}
          >
            <option value="">All departments</option>
            {(deptData?.departments || []).map((d) => (
              <option key={d.slug} value={d.slug}>{d.name}</option>
            ))}
          </select>
        )}
      </div>

      <div className={`rounded-lg border ${isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'} overflow-hidden`}>
        <table className="w-full text-sm">
          <thead className={`text-left ${isDark ? 'bg-gray-900/40 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
            <tr>
              <th className="px-4 py-2 font-rasa font-medium">Requester</th>
              <th className="px-4 py-2 font-rasa font-medium">Service</th>
              <th className="px-4 py-2 font-rasa font-medium">Status</th>
              <th className="px-4 py-2 font-rasa font-medium">Assignee</th>
              <th className="px-4 py-2 font-rasa font-medium">Submitted</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500 font-rasa">Loading…</td></tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-gray-500 font-rasa">No requests in this view.</td></tr>
            )}
            {rows.map((r) => {
              const st = STATUS_PILL[r.status] || STATUS_PILL.submitted;
              const Icon = st.icon;
              const primary = r.triage_state === 'pending'
                ? 'Unsure — needs triage'
                : r.items?.[0]?.label || 'Service request';
              return (
                <tr
                  key={r.public_id}
                  onClick={() => setSelected(r.public_id)}
                  className={`cursor-pointer ${isDark ? 'border-t border-gray-700 hover:bg-gray-700/40' : 'border-t border-gray-100 hover:bg-gray-50'}`}
                >
                  <td className="px-4 py-3 font-rasa">{r.requester?.name || r.requester?.email || '—'}</td>
                  <td className="px-4 py-3 font-rasa">
                    <div className="flex items-center gap-2">
                      {r.triage_state === 'pending' && <AlertCircle size={14} className="text-orange-500" />}
                      <span>{primary}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-rasa ${st.bg} ${st.text}`}>
                      <Icon size={12} /> {st.label}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-rasa text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {r.assigned_to || '—'}
                  </td>
                  <td className={`px-4 py-3 font-rasa text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {selected && (
        <ServiceRequestInboxDetailPanel publicId={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function FilterChip({ active, onClick, label, icon: Icon, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-rasa border transition ${
        active
          ? 'bg-blue-500 text-white border-blue-500'
          : isDark
            ? 'border-gray-700 text-gray-300 hover:border-gray-500'
            : 'border-gray-300 text-gray-700 hover:border-gray-400'
      }`}
    >
      {Icon && <Icon size={12} />} {label}
    </button>
  );
}
